import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Segmented, Tag, Empty } from 'antd';
import {
  edgeRunningLength, processEdgeKey, calculateChargeDimension, displayDimensionInUnit
} from './proformaHelpers';
import './edgePicker.css';

// ════════════════════════════════════════════════════════════════════
// Edge picker (RFT/RMT per-side billing) — Stage 6Q.
//
// A running-feet/metres #tag process (edge polish, beading, U-channel) should
// bill only the SIDES the work runs along. The pane IS the control: L1 top ·
// L2 right · L3 bottom · L4 left (the reference sketch). Opens EMPTY — the
// operator marks each worked edge deliberately (no accidental full-perimeter
// bill), Apply stays disabled until at least one edge is chosen. Selecting all
// four equals the full perimeter 2·(W+H).
//
// Each edge is labelled in the document's input unit (MM/INCH — display only,
// billing resolves in RFT/RMT). The charge/actual basis is decided ONCE in
// Company Defaults and shown read-only. The selection persists on the line
// (edge_detail JSON) for the value-add engine and the future Work Order print.
// ════════════════════════════════════════════════════════════════════

const ALL_SIDES = ['L1', 'L2', 'L3', 'L4'];
const RFT_UOMS = ['RFT', 'RMT'];
const BEAD = { L1: 't', L2: 'r', L3: 'b', L4: 'l' };
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (v) => `₹${(Math.round(num(v) * 100) / 100).toFixed(2)}`;

// The RFT/RMT processes on a line, each with its stable key + a friendly label.
function edgeProcesses(row) {
  const list = Array.isArray(row && row.processPricing) ? row.processPricing : [];
  return list
    .filter((p) => RFT_UOMS.includes(String((p && p.uom) || '').toUpperCase()) && num(p.rate) > 0)
    .map((p) => ({
      key: processEdgeKey(p),
      name: String((p && p.name) || '').trim() || '—',
      rate: num(p.rate),
      uom: String((p && p.uom) || '').toUpperCase()
    }));
}

// Resolve the width/height (mm) the edge lengths are measured on, per the basis.
function dimsForBasis(row, header) {
  const basis = String((header && header.edgeworkDimensionBasis) || 'CHARGE').toUpperCase();
  const aw = num(row && row.actualWidthMm);
  const ah = num(row && row.actualHeightMm);
  if (basis === 'ACTUAL') return { basis, wMm: aw, hMm: ah };
  const cw = aw > 0 ? calculateChargeDimension(aw, header && header.wastageMode, header && header.wastageWidthMm) : 0;
  const ch = ah > 0 ? calculateChargeDimension(ah, header && header.wastageMode, header && header.wastageHeightMm) : 0;
  return { basis, wMm: cw, hMm: ch };
}

export default function EdgePickerModal({ open, onClose, row, header, onSave }) {
  const unit = String((header && header.inputUnitCode) || 'MM').toUpperCase();
  const procs = useMemo(() => edgeProcesses(row), [row]);
  const { basis, wMm, hMm } = useMemo(() => dimsForBasis(row, header), [row, header]);
  const qty = Math.max(1, num(row && row.qty));

  // Working copy of the whole edge_detail map (keyed by process signature).
  const [detail, setDetail] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const firstPresetRef = useRef(null);

  // Land focus on the first quick preset (Width edges) when the picker opens, so
  // Tab flows Width → Height → All four → Clear before anything else.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => { if (firstPresetRef.current) firstPresetRef.current.focus(); }, 60);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const seed = {};
    const existing = (row && typeof row.edgeDetail === 'object' && row.edgeDetail) || {};
    for (const p of procs) {
      const prev = existing[p.key];
      // Re-opening a line keeps what was chosen; a fresh line opens EMPTY.
      const sides = prev && Array.isArray(prev.sides)
        ? prev.sides.map((s) => String(s).toUpperCase()).filter((s) => ALL_SIDES.includes(s))
        : [];
      seed[p.key] = { sides, basis, uom: p.uom };
    }
    setDetail(seed);
    setActiveKey(procs.length ? procs[0].key : null);
  }, [open, procs, basis]);

  const active = procs.find((p) => p.key === activeKey) || null;
  const activeSides = (active && detail[active.key] && detail[active.key].sides) || [];

  const setSides = (sides) => {
    if (!active) return;
    const ordered = ALL_SIDES.filter((s) => sides.includes(s));
    setDetail((d) => ({ ...d, [active.key]: { ...(d[active.key] || {}), sides: ordered, basis, uom: active.uom } }));
  };
  const toggleSide = (side) => {
    const cur = activeSides;
    setSides(cur.includes(side) ? cur.filter((s) => s !== side) : [...cur, side]);
  };

  // Edge length (document unit) for a side — the on-diagram labels.
  const sideLen = (side) => {
    const mm = (side === 'L1' || side === 'L3') ? wMm : hMm;
    if (mm <= 0) return '—';
    const v = displayDimensionInUnit(mm, unit);
    return v == null ? '—' : String(v);
  };
  const activeUom = active ? active.uom : 'RFT';
  const runLength = active ? edgeRunningLength(activeSides, wMm, hMm, activeUom) : 0;
  const amount = active ? Math.round(active.rate * runLength * qty * 100) / 100 : 0;
  const fullLength = active ? edgeRunningLength(ALL_SIDES, wMm, hMm, activeUom) : 0;
  const fullAmount = active ? Math.round(active.rate * fullLength * qty * 100) / 100 : 0;
  const selMm = activeSides.reduce((s, side) => s + ((side === 'L1' || side === 'L3') ? wMm : hMm), 0);
  const hasSelection = procs.some((p) => (detail[p.key] && detail[p.key].sides || []).length > 0);

  const save = () => {
    const out = {};
    for (const p of procs) {
      const sides = (detail[p.key] && detail[p.key].sides) || [];
      const len = edgeRunningLength(sides, wMm, hMm, p.uom);
      out[p.key] = {
        sides, basis,
        lengthRft: p.uom === 'RFT' ? len : 0,
        lengthRmt: p.uom === 'RMT' ? len : 0
      };
    }
    onSave(out);
  };

  const lineNo = row && (row.lineNo != null ? row.lineNo : row.displayNo);

  return (
    <Modal
      className="edge-picker"
      title={<span className="ep-title">
        <span className="ep-dot" />Choose edges{lineNo != null ? ` · Line ${lineNo}` : ''}
        <Tag className="ep-unit">{unit}</Tag>
      </span>}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Apply"
      okButtonProps={{ disabled: !hasSelection }}
      width={468}
      destroyOnClose
    >
      {!procs.length ? (
        <Empty description="This line has no running-feet (RFT/RMT) process to pick edges for." />
      ) : (
        <>
          {procs.length > 1 ? (
            <Segmented
              size="small" block className="ep-proc-seg"
              value={activeKey} onChange={setActiveKey}
              options={procs.map((p) => ({ label: p.name, value: p.key }))}
            />
          ) : null}

          {active ? (
            <div className="ep-proc">
              <span className="ep-pname">{active.name}</span>
              <span className="ep-prate">{money(active.rate).replace('.00', '')} / {active.uom}</span>
            </div>
          ) : null}

          <div className="ep-body">
            <div className="ep-fig">
              {activeSides.map((s) => <div key={s} className={`ep-bead ep-bead-${BEAD[s]}`} />)}
              <div className="ep-pane">
                <span className="ep-wd">W {sideLen('L1')}</span>
                <span className="ep-ht">H {sideLen('L2')}</span>
              </div>
              {!activeSides.length ? (
                <div className="ep-hint"><span>Tap an edge to start</span></div>
              ) : null}
              {ALL_SIDES.map((side) => {
                const on = activeSides.includes(side);
                return (
                  <button
                    type="button" key={side}
                    className={`ep-edge ep-${side.toLowerCase()}${on ? ' on' : ''}`}
                    aria-pressed={on}
                    aria-label={`Edge ${side} — ${sideLen(side)} ${unit}${on ? ', selected' : ''}`}
                    onClick={() => toggleSide(side)}
                  >
                    <span className="ep-k">{side}</span>
                    <span className="ep-len">{sideLen(side)}</span>
                    <span className="ep-tick">✓</span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeSides.length ? (
            <div className="ep-readout">
              <div className="ep-chips">{activeSides.map((s) => <span key={s} className="ep-rt">{s}</span>)}</div>
              <div className="ep-len">
                <b>{runLength.toFixed(2)} {activeUom}</b>
                <small>{selMm > 0 ? `${Math.round(selMm)} ${unit === 'MM' ? 'mm' : unit.toLowerCase()} selected` : ''}</small>
              </div>
              <div className="ep-amt">
                <b>{money(amount)}</b>
                <small>full perimeter {money(fullAmount)}</small>
              </div>
            </div>
          ) : (
            <div className="ep-readout empty">No edges selected yet — the price is ₹0 until you mark one.</div>
          )}

          {/* Focus starts on "Width edges" (see the open effect), so Tab flows
              Width → Height → All four → Clear → footer. All natural tabIndex, so
              AntD's focus trap keeps Tab inside the modal (positive tabIndex would
              break the trap and leak focus to the page behind). */}
          <div className="ep-quick">
            <button type="button" ref={firstPresetRef} onClick={() => setSides(['L1', 'L3'])}>Width edges</button>
            <button type="button" onClick={() => setSides(['L2', 'L4'])}>Height edges</button>
            <button type="button" onClick={() => setSides([...ALL_SIDES])}>All four</button>
            <button type="button" onClick={() => setSides([])}>Clear</button>
            <span className="ep-basis">{basis === 'ACTUAL' ? 'Actual size' : 'Charge size'} · Company Defaults</span>
          </div>
        </>
      )}
    </Modal>
  );
}
